import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Stack,
  Typography,
  TextField,
  CircularProgress,
  Alert,
  Box,
  Chip,
  LinearProgress,
} from '@mui/material';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import dayjs, { Dayjs } from 'dayjs';
import React, { useState, useEffect } from 'react';
import { useForm, FormProvider } from 'react-hook-form';

import {
  QuoteQuestion,
  useGetQuoteQuestions,
} from '@/api/endpoints/quote/useGenerateQuoteQuestions';
import { CheckCircleIcon } from '@/assets/icons/CheckCircleIcon';
import { ClipboardCheckIcon } from '@/assets/icons/ClipboardCheckIcon';
import { ClockIcon } from '@/assets/icons/ClockIcon';
import { Phone01Icon } from '@/assets/icons/Phone01Icon';
import { QuoteAnswerSessionService } from '@/services/QuoteAnswerSessionService';

import FormSelect, { Option } from '@/components/form/FormSelect';

interface QuoteRequirementsDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (answers: Record<number, string | number>, questions: QuoteQuestion[]) => void;
  searchQuery: string;
  vendorNames: string[];
  propertyAddress?: string;
  initialAnswers?: Record<number, string | number>;
}

const QuoteRequirementsDialog: React.FC<QuoteRequirementsDialogProps> = ({
  open,
  onClose,
  onSubmit,
  searchQuery,
  vendorNames,
  propertyAddress,
  initialAnswers,
}) => {
  const [answers, setAnswers] = useState<Record<number, string | number>>({});
  const [errors, setErrors] = useState<Record<number, string>>({});
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const methods = useForm({
    mode: 'onChange',
  });
  // Use cached query instead of mutation for better performance
  const quoteQuestionsQuery = useGetQuoteQuestions(
    searchQuery,
    vendorNames,
    propertyAddress,
    open // Enable when dialog is open
  );

  useEffect(() => {
    if (open) {
      // Reset state when dialog opens, but use initial answers if provided
      setAnswers(initialAnswers || {});
      setErrors({});
      setCurrentQuestionIndex(0);
      // Reset form state
      methods.reset();
    }
  }, [open, searchQuery, propertyAddress, initialAnswers, methods]);

  const handleAnswerChange = (questionId: number, value: string | number | Dayjs | null) => {
    if (value !== null) {
      // Convert Dayjs to ISO string for storage
      const storedValue = dayjs.isDayjs(value) ? value.toISOString() : value;
      setAnswers(prev => ({
        ...prev,
        [questionId]: storedValue,
      }));
      // Also update form state
      methods.setValue(`question-${questionId}`, storedValue);
    }
    // Clear error for this field
    setErrors(prev => {
      const newErrors = { ...prev };
      delete newErrors[questionId];
      return newErrors;
    });
  };

  const validateCurrentQuestion = () => {
    if (!quoteQuestionsQuery.data) return true;

    const currentQuestion = quoteQuestionsQuery.data.questions[currentQuestionIndex];
    if (currentQuestion.required && !answers[currentQuestion.id]) {
      setErrors({
        [currentQuestion.id]: 'This field is required',
      });
      return false;
    }
    return true;
  };

  const handleNext = () => {
    if (validateCurrentQuestion()) {
      setCurrentQuestionIndex(prev => prev + 1);
    }
  };

  const handleBack = () => {
    setCurrentQuestionIndex(prev => prev - 1);
  };

  const handleSubmit = () => {
    if (!validateCurrentQuestion()) return;

    setIsSubmitting(true);
    try {
      // Store answers for future use in session storage
      if (quoteQuestionsQuery.data) {
        QuoteAnswerSessionService.saveAnswers(
          searchQuery,
          answers,
          quoteQuestionsQuery.data.questions,
          quoteQuestionsQuery.data.service_type,
          propertyAddress
        );
      }

      onSubmit(answers, quoteQuestionsQuery.data?.questions || []);
      onClose();
    } catch (error) {
      // Error is handled in parent component
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderQuestionInput = (question: QuoteQuestion) => {
    const value = answers[question.id] || '';
    const hasError = !!errors[question.id];

    switch (question.input_type) {
    case 'select': {
      const selectOptions: Option[] = question.options?.map(option => ({
        label: option,
        value: option,
      })) || [];

      // Set initial form value if we have an answer
      if (value && !methods.getValues(`question-${question.id}`)) {
        methods.setValue(`question-${question.id}`, value as string);
      }

      return (
        <FormProvider {...methods}>
          <FormSelect
            name={`question-${question.id}`}
            label={question.text}
            options={selectOptions}
            placeholder="Select an option"
            required={question.required}
            error={hasError}
            helperText={errors[question.id]}
            defaultValue={value as string}
            control={methods.control}
            muiSelectProps={{
              onChange: (e) => {
                const selectedValue = e.target.value as string;
                handleAnswerChange(question.id, selectedValue);
              },
              MenuProps: {
                PaperProps: {
                  style: {
                    maxHeight: 200,
                  },
                },
              },
            }}
          />
        </FormProvider>
      );
    }

    case 'text':
      return (
        <TextField
          fullWidth
          multiline
          rows={4}
          label={question.text}
          value={value}
          onChange={e => handleAnswerChange(question.id, e.target.value)}
          placeholder={question.placeholder}
          error={hasError}
          helperText={errors[question.id]}
          required={question.required}
        />
      );

    case 'date':
      return (
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <DatePicker
            label={question.text}
            value={value ? dayjs(value as string) : null}
            onChange={newValue => handleAnswerChange(question.id, newValue)}
            slotProps={{
              textField: {
                fullWidth: true,
                error: hasError,
                helperText: errors[question.id],
                required: question.required,
              },
              popper: {
                placement: 'bottom-start',
                modifiers: [
                  {
                    name: 'preventOverflow',
                    options: {
                      boundary: 'viewport',
                    },
                  },
                ],
              },
            }}
          />
        </LocalizationProvider>
      );

    case 'number':
      return (
        <TextField
          fullWidth
          type="number"
          label={question.text}
          value={value}
          onChange={e => handleAnswerChange(question.id, e.target.value)}
          placeholder={question.placeholder}
          error={hasError}
          helperText={errors[question.id]}
          required={question.required}
          slotProps={{
            htmlInput: {
              min: question.min,
              max: question.max,
            },
          }}
        />
      );

    default:
      return null;
    }
  };

  const isLastQuestion = quoteQuestionsQuery.data
    ? currentQuestionIndex >= quoteQuestionsQuery.data.questions.length - 1
    : false;

  const progress = quoteQuestionsQuery.data
    ? ((currentQuestionIndex + 1) / quoteQuestionsQuery.data.questions.length) * 100
    : 0;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      keepMounted
      disableScrollLock
      slotProps={{
        paper: {
          sx: {
            maxHeight: '90vh',
            overflow: 'visible',
          },
        },
      }}
    >
      <DialogTitle>
        <Stack spacing={1}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <ClipboardCheckIcon width={24} height={24} />
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Help Us Get You Accurate Quotes
            </Typography>
          </Stack>
          {quoteQuestionsQuery.data && (
            <Stack spacing={1}>
              <Typography variant="body2" color="text.secondary">
                Service: {quoteQuestionsQuery.data.service_type}
              </Typography>
              <Stack direction="row" spacing={1} alignItems="center">
                <ClockIcon width={16} height={16} />
                <Typography variant="caption" color="text.secondary">
                  {quoteQuestionsQuery.data.total_questions} questions
                </Typography>
              </Stack>
            </Stack>
          )}
        </Stack>
      </DialogTitle>

      <DialogContent sx={{ overflow: 'visible' }}>
        <Stack spacing={3}>
          {/* Vendor chips */}
          {vendorNames.length > 0 && (
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ mb: 1 }}>
                Getting quotes from:
              </Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap">
                {vendorNames.map(name => (
                  <Chip
                    key={name}
                    label={name}
                    size="small"
                    icon={<CheckCircleIcon width={16} height={16} />}
                  />
                ))}
              </Stack>
            </Box>
          )}

          {/* Progress bar */}
          {quoteQuestionsQuery.data && (
            <Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="caption" color="text.secondary">
                  Question {currentQuestionIndex + 1} of {quoteQuestionsQuery.data.questions.length}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {Math.round(progress)}% Complete
                </Typography>
              </Box>
              <LinearProgress variant="determinate" value={progress} />
            </Box>
          )}

          {/* Loading state */}
          {quoteQuestionsQuery.isPending && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          )}

          {/* Error state */}
          {quoteQuestionsQuery.isError && (
            <Alert severity="error">
              Failed to load questions. Please try again.
              {quoteQuestionsQuery.error && (
                <Box sx={{ mt: 1, fontSize: '0.875rem' }}>
                  Error: {quoteQuestionsQuery.error.message}
                </Box>
              )}
            </Alert>
          )}

          {/* Current question */}
          {quoteQuestionsQuery.data && quoteQuestionsQuery.data.questions && (
            <Box>
              {renderQuestionInput(quoteQuestionsQuery.data.questions[currentQuestionIndex])}
            </Box>
          )}

          {/* Info message */}
          <Alert severity="info" icon={<Phone01Icon width={20} height={20} />}>
            <Typography variant="body2">
              Terri will use this information to get you accurate quotes from providers. The more
              details you provide, the better the quotes!
            </Typography>
          </Alert>
        </Stack>
      </DialogContent>

      <DialogActions sx={{ p: 3 }}>
        <Button onClick={onClose} disabled={isSubmitting}>
          Cancel
        </Button>
        {currentQuestionIndex > 0 && (
          <Button onClick={handleBack} disabled={isSubmitting}>
            Back
          </Button>
        )}
        {!isLastQuestion ? (
          <Button
            variant="contained"
            onClick={handleNext}
            disabled={!quoteQuestionsQuery.data || isSubmitting}
          >
            Next
          </Button>
        ) : (
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={!quoteQuestionsQuery.data || isSubmitting}
            startIcon={
              isSubmitting ? <CircularProgress size={20} /> : <Phone01Icon width={20} height={20} />
            }
          >
            {isSubmitting ? 'Preparing...' : 'Start Calls'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default QuoteRequirementsDialog;
